<script lang="ts">
  import { onMount } from 'svelte'
  import { api } from '$lib/api/client'
  import type { paths } from '$lib/api/generated'
  import Table from '../../components/Table.svelte'
  import SearchInput from '../../components/SearchInput.svelte'
  import Modal from '../../components/Modal.svelte'
  import FormField from '../../components/FormField.svelte'
  import { showApiError, showError } from '$lib/util/errors'
  import { pushToast } from '$lib/util/toasts'
  import { debounce } from '$lib/util/debounce'

  type PropertyManager =
    paths['/property-managers']['get']['responses'][200]['content']['application/json']['items'][number]
  type Property =
    paths['/property-managers/{id}/properties']['get']['responses'][200]['content']['application/json']['items'][number]

  let items = $state<PropertyManager[]>([])
  let nextCursor = $state<number | undefined>(undefined)
  let search = $state('')
  let loading = $state(false)
  let showModal = $state(false)
  let editing = $state<PropertyManager | null>(null)

  let name = $state('')
  let email = $state('')
  let phone = $state('')
  let company = $state('')

  // Branding (only editable on an existing PM - create has no branding fields)
  let displayName = $state('')
  let slug = $state('')
  let accentColor = $state('')
  let supportEmail = $state('')
  let supportPhone = $state('')
  let logoS3Key = $state<string | null>(null)

  // Properties under the selected PM
  let properties = $state<Property[]>([])
  let editingProperty = $state<Property | null>(null)
  let propName = $state('')
  let propSlug = $state('')
  let propWelcome = $state('')

  async function load(cursor?: number): Promise<void> {
    loading = true
    const { data, error } = await api.GET('/property-managers', {
      params: {
        query: {
          limit: 50,
          ...(cursor !== undefined ? { cursor } : {}),
          ...(search ? { search } : {}),
        },
      },
    })
    if (error) {
      showApiError(error)
      loading = false
      return
    }
    items = cursor !== undefined ? [...items, ...(data?.items ?? [])] : (data?.items ?? [])
    nextCursor = data?.nextCursor
    loading = false
  }

  function resetProperties(): void {
    properties = []
    editingProperty = null
    propName = ''
    propSlug = ''
    propWelcome = ''
  }

  function openCreate(): void {
    editing = null
    name = ''
    email = ''
    phone = ''
    company = ''
    displayName = ''
    slug = ''
    accentColor = ''
    supportEmail = ''
    supportPhone = ''
    logoS3Key = null
    resetProperties()
    showModal = true
  }

  function openEdit(pm: PropertyManager): void {
    editing = pm
    name = pm.name
    email = pm.email
    phone = pm.phone ?? ''
    company = pm.company ?? ''
    displayName = pm.displayName ?? ''
    slug = pm.slug ?? ''
    accentColor = pm.accentColor ?? ''
    supportEmail = pm.supportEmail ?? ''
    supportPhone = pm.supportPhone ?? ''
    logoS3Key = pm.logoS3Key
    resetProperties()
    void loadProperties(pm.id)
    showModal = true
  }

  async function handleSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault()
    if (editing) {
      // accentColor: omit when empty (an empty string fails the #rrggbb pattern -> 400).
      // Other optional branding fields send null when cleared.
      // Every optional field sends null when cleared so it can be unset. accentColor is
      // the one exception it cannot send an empty string (fails the #rrggbb pattern), so
      // an empty value becomes null (clears) and a non-empty value is validated server-side.
      const body = {
        name,
        email,
        phone: phone.trim() || null,
        company: company.trim() || null,
        displayName: displayName.trim() || null,
        slug: slug.trim() || null,
        supportEmail: supportEmail.trim() || null,
        supportPhone: supportPhone.trim() || null,
        accentColor: accentColor.trim() || null,
      }
      const { data, error } = await api.PUT('/property-managers/{id}', {
        params: { path: { id: editing.id } },
        body,
      })
      if (error || !data) {
        showApiError(error)
        return
      }
      items = items.map((p) => (p.id === editing?.id ? data : p))
    } else {
      const body = {
        name,
        email,
        ...(phone ? { phone } : {}),
        ...(company ? { company } : {}),
      }
      const { data, error } = await api.POST('/property-managers', { body })
      if (error || !data) {
        showApiError(error)
        return
      }
      items = [data, ...items]
    }
    showModal = false
  }

  async function loadProperties(pmId: number): Promise<void> {
    const { data, error } = await api.GET('/property-managers/{id}/properties', {
      params: { path: { id: pmId } },
    })
    if (error) {
      showApiError(error)
      return
    }
    properties = data?.items ?? []
  }

  function editProperty(p: Property): void {
    editingProperty = p
    propName = p.name
    propSlug = p.slug
    propWelcome = p.welcomeMessage ?? ''
  }

  function cancelPropertyEdit(): void {
    editingProperty = null
    propName = ''
    propSlug = ''
    propWelcome = ''
  }

  async function saveProperty(e: SubmitEvent): Promise<void> {
    e.preventDefault()
    if (!editing) return
    if (editingProperty) {
      const { data, error } = await api.PUT('/properties/{id}', {
        params: { path: { id: editingProperty.id } },
        body: {
          name: propName.trim(),
          slug: propSlug.trim(),
          welcomeMessage: propWelcome.trim() || null,
        },
      })
      if (error || !data) {
        showApiError(error)
        return
      }
      properties = properties.map((p) => (p.id === data.id ? data : p))
    } else {
      const { data, error } = await api.POST('/property-managers/{id}/properties', {
        params: { path: { id: editing.id } },
        body: {
          name: propName.trim(),
          slug: propSlug.trim(),
          ...(propWelcome.trim() ? { welcomeMessage: propWelcome.trim() } : {}),
        },
      })
      if (error || !data) {
        showApiError(error)
        return
      }
      properties = [...properties, data]
    }
    cancelPropertyEdit()
  }

  async function deleteProperty(id: number): Promise<void> {
    if (!confirm('Delete this property?')) return
    const { error } = await api.DELETE('/properties/{id}', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    properties = properties.filter((p) => p.id !== id)
    if (editingProperty?.id === id) cancelPropertyEdit()
  }

  async function uploadLogo(pmId: number, file: File): Promise<void> {
    const presign = await api.POST('/property-managers/{id}/logo-upload-url', {
      params: { path: { id: pmId } },
      body: { contentType: file.type, sizeBytes: file.size },
    })
    if (!presign.data) {
      // Surface the server's actual reason (e.g. "Logo too large", "Unsupported logo type").
      showApiError(presign.error)
      return
    }
    const put = await fetch(presign.data.url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    })
    if (!put.ok) {
      showError({ detail: 'Logo upload failed' })
      return
    }
    const s3Key = presign.data.s3Key
    const { data, error } = await api.PUT('/property-managers/{id}', {
      params: { path: { id: pmId } },
      body: { logoS3Key: s3Key },
    })
    if (error || !data) {
      showApiError(error)
      return
    }
    logoS3Key = s3Key
    items = items.map((p) => (p.id === pmId ? data : p))
    pushToast('success', 'Logo uploaded')
  }

  async function handleLogoChange(e: Event): Promise<void> {
    const input = e.currentTarget as HTMLInputElement
    const file = input.files?.[0]
    if (file && editing) await uploadLogo(editing.id, file)
    input.value = ''
  }

  async function removeLogo(): Promise<void> {
    if (!editing) return
    const { data, error } = await api.PUT('/property-managers/{id}', {
      params: { path: { id: editing.id } },
      body: { logoS3Key: null },
    })
    if (error || !data) {
      showApiError(error)
      return
    }
    logoS3Key = null
    items = items.map((p) => (p.id === editing?.id ? data : p))
    pushToast('success', 'Logo removed')
  }

  async function copyLink(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text)
      pushToast('success', 'Link copied')
    } catch {
      pushToast('error', 'Could not copy link')
    }
  }

  async function handleDelete(id: number): Promise<void> {
    if (!confirm('Delete this property manager?')) return
    const { error } = await api.DELETE('/property-managers/{id}', { params: { path: { id } } })
    if (error) {
      showApiError(error)
      return
    }
    items = items.filter((p) => p.id !== id)
  }

  const searchDebounced = debounce(() => void load(), 300)
  $effect(() => {
    void search
    searchDebounced.call()
    return () => searchDebounced.cancel()
  })

  onMount(() => {
    void load()
  })
</script>

<div class="section-header">
  <h1>Property Managers</h1>
  <div class="section-actions">
    <SearchInput bind:value={search} placeholder="Search name, email, or company" />
    <button type="button" class="primary" onclick={openCreate}>+ Add PM</button>
  </div>
</div>

<Table
  {items}
  {loading}
  columns={[
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'company', label: 'Company' },
  ]}
  onEdit={openEdit}
  onDelete={handleDelete}
/>

{#if nextCursor !== undefined}
  <button type="button" class="load-more" onclick={() => void load(nextCursor)} disabled={loading}>
    {loading ? 'Loading…' : 'Load more'}
  </button>
{/if}

{#if showModal}
  <Modal
    title={editing ? 'Edit property manager' : 'Add property manager'}
    onclose={() => (showModal = false)}
  >
    <form onsubmit={handleSubmit}>
      <FormField label="Name" name="name" bind:value={name} required />
      <FormField label="Email" name="email" type="email" bind:value={email} required />
      <FormField label="Phone" name="phone" bind:value={phone} />
      <FormField label="Company" name="company" bind:value={company} />

      {#if editing}
        <fieldset class="panel">
          <legend>Branded intake</legend>
          <FormField label="Display name" name="displayName" bind:value={displayName} />
          <FormField label="Slug" name="slug" bind:value={slug} />
          {#if slug.trim()}
            <p class="hint">
              Branded link base: <code>/register/{slug.trim()}/&lt;property-slug&gt;</code>
            </p>
          {/if}
          <div class="form-field">
            <label for="accentColor">Accent color</label>
            <div class="color-row">
              <input
                type="color"
                aria-label="Accent color picker"
                value={accentColor.trim() || '#000000'}
                oninput={(e) => (accentColor = e.currentTarget.value)}
              />
              <input
                id="accentColor"
                name="accentColor"
                type="text"
                placeholder="#rrggbb"
                bind:value={accentColor}
              />
              {#if accentColor.trim()}
                <button type="button" class="link-btn" onclick={() => (accentColor = '')}>Clear</button>
              {/if}
            </div>
          </div>
          <FormField label="Support email" name="supportEmail" type="email" bind:value={supportEmail} />
          <FormField label="Support phone" name="supportPhone" bind:value={supportPhone} />
          <div class="form-field">
            <label for="logo">Logo</label>
            <input
              id="logo"
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              onchange={handleLogoChange}
            />
            {#if logoS3Key}
              <div class="link-row">
                <span class="hint">Current: <code>{logoS3Key}</code></span>
                <button type="button" class="link-btn danger" onclick={() => void removeLogo()}>
                  Remove logo
                </button>
              </div>
            {/if}
          </div>
        </fieldset>
      {/if}

      <button type="submit" class="primary">Save</button>
    </form>

    {#if editing}
      <section class="panel properties">
        <h3>Properties</h3>
        {#if properties.length === 0}
          <p class="hint">No properties yet.</p>
        {/if}
        <ul class="property-list">
          {#each properties as p (p.id)}
            <li>
              <div class="property-info">
                <strong>{p.name}</strong>
                <span class="hint">{p.slug}</span>
                {#if editing.slug}
                  <div class="link-row">
                    <code>/register/{editing.slug}/{p.slug}</code>
                    <button
                      type="button"
                      class="link-btn"
                      onclick={() =>
                        void copyLink(`${location.origin}/register/${editing?.slug}/${p.slug}`)}
                    >
                      Copy
                    </button>
                  </div>
                {/if}
              </div>
              <div class="property-actions">
                <button type="button" class="link-btn" onclick={() => editProperty(p)}>Edit</button>
                <button type="button" class="link-btn danger" onclick={() => void deleteProperty(p.id)}>
                  Delete
                </button>
              </div>
            </li>
          {/each}
        </ul>

        <form onsubmit={saveProperty} class="property-form">
          <h4>{editingProperty ? 'Edit property' : 'Add property'}</h4>
          <FormField label="Property name" name="propName" bind:value={propName} required />
          <FormField label="Property slug" name="propSlug" bind:value={propSlug} required />
          <FormField label="Welcome message" name="propWelcome" bind:value={propWelcome} />
          <div class="property-form-actions">
            <button type="submit" class="primary">{editingProperty ? 'Update' : 'Add'}</button>
            {#if editingProperty}
              <button type="button" class="link-btn" onclick={cancelPropertyEdit}>Cancel</button>
            {/if}
          </div>
        </form>
      </section>
    {/if}
  </Modal>
{/if}

<style>
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }
  h1 {
    margin: 0;
    font-size: 1.75rem;
  }
  .section-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  button.primary {
    background: var(--brand);
    color: white;
    padding: 6px 14px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.875rem;
  }
  button.primary:hover {
    background: var(--brand-hover);
  }
  button.load-more {
    padding: 8px 16px;
    background: var(--surface-2);
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    cursor: pointer;
  }
  .panel {
    border: 1px solid var(--line-strong);
    border-radius: 6px;
    padding: 12px 16px;
    margin: 16px 0;
  }
  fieldset.panel legend {
    font-weight: 600;
    padding: 0 6px;
  }
  .panel h3 {
    margin: 0 0 8px;
    font-size: 1.1rem;
  }
  .panel h4 {
    margin: 12px 0 8px;
    font-size: 0.95rem;
  }
  .hint {
    font-size: 0.8rem;
    color: var(--muted);
    margin: 4px 0;
  }
  .form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
  }
  .form-field label {
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--ink);
  }
  .color-row {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  .color-row input[type='color'] {
    width: 44px;
    height: 44px;
    padding: 0;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    cursor: pointer;
  }
  .color-row input[type='text'] {
    flex: 1;
    padding: 10px 12px;
    min-height: 44px;
    box-sizing: border-box;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    font-size: 1rem;
  }
  .property-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .property-list li {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    padding: 8px 10px;
  }
  .property-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .link-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
  .property-actions {
    display: flex;
    gap: 8px;
  }
  .link-btn {
    background: none;
    border: none;
    color: var(--brand);
    cursor: pointer;
    font-size: 0.85rem;
    padding: 2px 4px;
    text-decoration: underline;
  }
  .link-btn.danger {
    color: var(--err-ink);
  }
  .link-btn:focus-visible {
    outline: 2px solid var(--brand);
    outline-offset: 2px;
  }
  .property-form-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }
</style>
